import React, { useEffect, useState } from "react";
import {
Form,
Input,
Select,
Button,
Drawer,
DatePicker,
Upload,
message,
Spin,
} from "antd";
import { InboxOutlined, CloseOutlined } from "@ant-design/icons";
import {
useGetLicensorsPartnersListQuery,
} from "../../appRedux/api/adminPartnerApi";
import {
useGetSingleInvoiceRequestQuery,
useAddInvoiceRequestMutation,
useEditInvoiceRequestMutation,
} from "../../appRedux/api/partnerInvoiceApi";
import "./style.css";
import moment from "moment";

const { Dragger } = Upload;
const { Option } = Select;

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 5 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 18 },
    },
    };

    // Constants for validation
    const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB in bytes

    const LicensorAddEditInvoiceDrawer = ({ 
    visible, 
    onClose, 
    editMode, 
    requestId,
    onSuccess
    }) => {
    const [editFlag, setEditFlag] = useState(false);
    const [editedFields, setEditedFields] = useState({});
    const [removedFiles, setRemovedFiles] = useState([]);
    const [initialFiles, setInitialFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [form] = Form.useForm();

    // RTK Query hooks
    const { data: royaltyPartnersList = [], isLoading: partnersLoading } = useGetLicensorsPartnersListQuery(
        undefined,
        { skip: !visible }
    );
    
    const { data: editInvoiceRequestData, isLoading: editDataLoading, refetch: refetchEditData } = useGetSingleInvoiceRequestQuery(
        requestId,
        { skip: !requestId }
    );

    const [addInvoiceRequest] = useAddInvoiceRequestMutation();
    const [editInvoiceRequest] = useEditInvoiceRequestMutation();

    const invoiceQuarter = [
        { id: 1, quarter_name: "Q1" },
        { id: 2, quarter_name: "Q2" },
        { id: 3, quarter_name: "Q3" },
        { id: 4, quarter_name: "Q4" },
    ];

    const handleFieldChange = (changedValues) => {
        setEditedFields((prevEditedFields) => ({
        ...prevEditedFields,
        ...changedValues,
        }));
    };

    useEffect(() => {
        if (editMode && requestId) {
        setEditFlag(true);
        } else {
        setEditFlag(false);
        }
    }, [editMode, requestId]);

    // Trigger refetch when drawer opens in edit mode with a requestId
    useEffect(() => {
        if (visible && editMode && requestId && refetchEditData) {
        refetchEditData();
        }
    }, [visible, editMode, requestId, refetchEditData]);

    useEffect(() => {
        if (editInvoiceRequestData && editMode && visible) {
        // Set initial files for edit mode
        const initialFiles = editInvoiceRequestData?.invoice_file?.map((file) => ({
            uid: file.id,
            name: file.document.split("/").pop(),
            status: "done",
            url: file.document,
            documentId: file.id, // Store document ID for deletion tracking
        })) || [];
        
        setInitialFiles(initialFiles);
        
        form.setFieldsValue({
            partner: editInvoiceRequestData?.partner_id?.partner_id,
            year: editInvoiceRequestData?.year 
            ? moment(`${editInvoiceRequestData?.year}-01-01T00:00:00.000Z`)
            : null,
            quarter: editInvoiceRequestData?.quarter,
            issue_date: editInvoiceRequestData?.issue_date 
            ? moment(editInvoiceRequestData?.issue_date)
            : null,
            invoice_due_date: editInvoiceRequestData?.invoice_due_date 
            ? moment(editInvoiceRequestData?.invoice_due_date)
            : null,
            amount: editInvoiceRequestData?.amount,
            invoice_file: initialFiles,
        });
        } else if (!editMode && visible) {
        // Reset for add mode
        form.resetFields();
        setRemovedFiles([]);
        setInitialFiles([]);
        }
    }, [editInvoiceRequestData, editMode, visible, form]);

    const handleRemove = (file) => {
        // If file has a documentId (existing file), add to removedFiles for deletion
        if (file.documentId) {
        setRemovedFiles(prev => [...prev, file.documentId]);
        }
        return true; // Allow removal from UI
    };

    const normFile = (e) => {
        if (Array.isArray(e)) {
        return e;
        }
        return e && e.fileList;
    };

    // Function to count new files (files with originFileObj)
    const countNewFiles = (fileList) => {
        return fileList.filter(file => file.originFileObj).length;
    };

    const checkFile = (file, fileList) => {
        const allowedFileTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        ];
        const isTypeValid = allowedFileTypes.includes(file.type);
        const isSizeValid = file.size <= MAX_FILE_SIZE;

        if (!isTypeValid) {
        message.error("Please upload a PDF, JPEG, or PNG document!");
        return Upload.LIST_IGNORE;
        } else if (!isSizeValid) {
        message.error("File size must be less than or equal to 15 MB!");
        return Upload.LIST_IGNORE;
        }

        // Return false to prevent automatic upload
        return false;
    };

    const onFinish = async (values) => {
        setIsSubmitting(true);
        try {
        const formData = new FormData();
        
        // Final validation before submission
        const fileList = values.invoice_file || [];
        const newFileCount = countNewFiles(fileList);

        // For new requests, ensure at least one file is uploaded
        if (!editMode && newFileCount === 0) {
            message.error("Please upload at least one invoice file");
            setIsSubmitting(false);
            return;
        }

        // For edit mode, if all files are removed, show error
        if (editMode && fileList.length === 0) {
            message.error("Please upload at least one invoice file");
            setIsSubmitting(false);
            return;
        }

        if (!editMode) {
            // Add new invoice
            values.invoice_file.forEach((data) => {
            if (data.originFileObj) {
                formData.append("invoice_file", data.originFileObj);
            }
            });
            formData.append("issue_date", values?.issue_date.toISOString());
            formData.append(
            "invoice_due_date",
            values?.invoice_due_date.toISOString()
            );
            formData.append("amount", values.amount);
            formData.append("year", moment(values.year.toISOString())?.year());
            formData.append("quarter", values.quarter);
            formData.append("partner_id", values.partner);
            
            await addInvoiceRequest(formData).unwrap();
        } else {
            // Edit existing invoice
            
            // Handle file uploads
            const currentFiles = values.invoice_file || [];
            const newFiles = currentFiles.filter(file => file.originFileObj);
            newFiles.forEach(file => {
            formData.append("invoice_file", file.originFileObj);
            });

            // Add other fields
            formData.append("issue_date", values.issue_date.toISOString());
            formData.append("invoice_due_date", values.invoice_due_date.toISOString());
            formData.append("amount", values.amount);
            formData.append("year", moment(values.year.toISOString())?.year());
            formData.append("quarter", values.quarter);
            formData.append("partner_id", values.partner);
            
            // Add files_to_delete parameter for removed files (comma-separated)
            if (removedFiles.length > 0) {
            formData.append("files_to_delete", removedFiles.join(','));
            }
            
            formData.append("licensor_status", "PENDING");
            
            await editInvoiceRequest({ id: requestId, data: formData }).unwrap();
        }
        
        handleClose();
        if (onSuccess) {
            onSuccess();
        }
        } catch (error) {
        console.error("Error saving invoice:", error);
        message.error(
            error?.data?.message || 
            `Failed to ${editMode ? 'update' : 'submit'} invoice. Please try again.`
        );
        } finally {
        setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        setRemovedFiles([]);
        setInitialFiles([]);
        setEditedFields({});
        setIsSubmitting(false);
        onClose();
    };

    const isLoading = isSubmitting || partnersLoading || editDataLoading;

    return (
        <Drawer
        className="licensor-add-edit-invoice-drawer"
        title={null}
        placement="right"
        width={1000}
        onClose={isLoading ? () => {} : handleClose}
        open={visible}
        closable={false}
        footer={
            <div className="drawer-footer" style={{ textAlign: 'right' }}>
            <Button onClick={handleClose} style={{ marginRight: 8 }} disabled={isLoading}>
                Cancel
            </Button>
            <Button 
                type="primary" 
                onClick={() => form.submit()} 
                disabled={isLoading}
                loading={isSubmitting}
            >
                {editMode ? 'Save' : 'Submit Request'}
            </Button>
            </div>
        }
        >
        {isLoading && (
            <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
            }}>
            <Spin size="large" />
            </div>
        )}
        
        <div className="drawer-header">
            <span>{editMode ? "Edit Invoice" : "Add Invoice"}</span>
            <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleClose}
            disabled={isLoading}
            />
        </div>

        <Form
            {...formItemLayout}
            form={form}
            name="add-edit-invoice"
            onValuesChange={handleFieldChange}
            onFinish={onFinish}
            disabled={isLoading}
            scrollToFirstError
        >
            <Form.Item
            label="Year"
            name="year"
            rules={[
                {
                required: true,
                message: "Please select a year!",
                },
            ]}
            >
            <DatePicker picker="year" style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
            name="quarter"
            label="Quarter"
            rules={[
                { required: true, message: "Please select a quarter" },
            ]}
            >
            <Select placeholder="Select a quarter">
                {invoiceQuarter.map((invoiceQuarter) => (
                <Option
                    key={invoiceQuarter.id}
                    value={invoiceQuarter.quarter_name}
                >
                    {invoiceQuarter.quarter_name}
                </Option>
                ))}
            </Select>
            </Form.Item>

            <Form.Item
            label="Partner"
            name="partner"
            rules={[
                {
                required: true,
                message: "Please select a partner!",
                },
            ]}
            >
            <Select placeholder="Select a partner" 
                loading={partnersLoading}
            >
                {Array.isArray(royaltyPartnersList) && royaltyPartnersList.map((partner) => (
                <Option
                    key={partner.partner_id}
                    value={partner.partner_id}
                >
                    {partner.company_name}
                </Option>
                ))}
            </Select>
            </Form.Item>

            <Form.Item
            label="Amount"
            name="amount"
            rules={[
                {
                required: true,
                message: "Please enter the amount!",
                },
                ({ getFieldValue }) => ({
                validator(_, value) {
                    const inputValue = parseInt(value, 10);
                    if (
                    !isNaN(inputValue) &&
                    inputValue >= 0 &&
                    inputValue <= 99000000
                    ) {
                    return Promise.resolve();
                    }
                    return Promise.reject(
                    new Error("Amount must be between 0 and 99Mn")
                    );
                },
                }),
            ]}
            >
            <Input name="amount" type="number" />
            </Form.Item>

            <Form.Item
            label="Issue Date"
            name="issue_date"
            rules={[
                { required: true, message: "Please select a date!" },
            ]}
            >
            <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            
            <Form.Item
            label="Invoice Due Date"
            name="invoice_due_date"
            rules={[
                { required: true, message: "Please select a date!" },
            ]}
            >
            <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
            label="Invoice"
            name="invoice_file"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[
                { 
                required: true, 
                message: "" 
                },
                ({ getFieldValue }) => ({
                validator(_, value) {
                    const fileList = value || [];
                    
                    if (fileList.length === 0) {
                    return Promise.reject(new Error("Please upload at least one invoice file"));
                    }

                    // Check each file
                    for (const file of fileList) {
                    // Only validate new files (those with originFileObj)
                    if (file.originFileObj) {
                        const allowedFileTypes = [
                        "application/pdf",
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                        ];
                        
                        if (!allowedFileTypes.includes(file.originFileObj.type)) {
                        return Promise.reject(
                            new Error(`Invalid file type for ${file.name}. Please upload PDF, JPEG, or PNG documents only!`)
                        );
                        }

                        if (file.originFileObj.size > MAX_FILE_SIZE) {
                        return Promise.reject(
                            new Error(`File ${file.name} exceeds 15MB size limit!`)
                        );
                        }
                    }
                    }

                    return Promise.resolve();
                },
                }),
            ]}
            >
            <Dragger
                multiple={true}
                beforeUpload={(file, fileList) => {
                return checkFile(file, fileList);
                }}
                accept=".pdf, .png, .jpeg, .jpg"
                onRemove={handleRemove}
                defaultFileList={editMode ? initialFiles : []}
                listType="text"
            >
                <p className="ant-upload-drag-icon">
                <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                Click or drag files to this area to upload
                </p>
                <p className="ant-upload-hint">
                Support for a single or bulk upload (pdf, png, jpeg). 
                Max file size: 15MB per file.
                </p>
            </Dragger>
            </Form.Item>
        </Form>
        </Drawer>
    );
};

export default LicensorAddEditInvoiceDrawer;